import { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import countryTelephoneData from 'country-telephone-data'

import useTranslations from '../../../../i18n/useTranslations'

import {
  Alert,
  Box,
  Button,
  FormHelperText,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'

import { createFullAddress } from '../../../../ui/views/shippingSend/Step5Summary'

// import TooltipButton from '../../../../ui/components/tooltipButton'
import Autocomplete from '../../../../ui/components/autocomplete'
import Select from '../../../../ui/components/select'

import { ReactComponent as WarningCircleFillIcon } from '../../../../assets/icons/warning-circle-fill.svg'
import useStyles from './styles'
import { useAuthedFetch } from '../../../../auth/useAuthedFetch'

import GooglePlacesAutocomplete from './GooglePlacesAutocomplete'
import XLSX from 'xlsx'
import provincesFile from './stateOrProvincecode.xlsx'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { useDestinationCountries } from '../../../../services/destinationCountries'
import './NewAddress.styles.css'

const propTypes = {
  clientCode: PropTypes.string,
  centerCode: PropTypes.string,
  zeroResultsGeocode: PropTypes.bool,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      code: PropTypes.string,
    }),
  ),
  values: PropTypes.shape({
    address: PropTypes.shape({
      country: PropTypes.object,
      address: PropTypes.string,
      name: PropTypes.string,
      phone: PropTypes.string,
      phonePrefix: PropTypes.string,
      email: PropTypes.string,
      stair: PropTypes.string,
      floor: PropTypes.string,
      letter: PropTypes.string,
      postalCode: PropTypes.string,
      province: PropTypes.string,
      city: PropTypes.string,
      comments: PropTypes.string,
      contactClientCode: PropTypes.string,
      hasSeveralLocalities: PropTypes.bool,
      localitiesOptions: PropTypes.arrayOf(PropTypes.string),
    }),
    comments: PropTypes.string,
  }),
  errors: PropTypes.object,
  isPhoneRequired: PropTypes.bool,
  autofillAddress: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  setErrors: PropTypes.func,
}

const Address = ({
  clientCode,
  isPhoneRequired,
  zeroResultsGeocode,
  centerCode,
  onChange,
  onBlur,
  values,
  countries,
  autofillAddress,
  errors,
  setErrors,
}) => {
  const [savingStatus, setSavingStatus] = useState({
    loading: false,
    error: false,
    success: false,
  })
  const classes = useStyles()
  const formatMessage = useTranslations()
  const authedFetch = useAuthedFetch()

  const [provicesByCountryCode, setProvincesByCountryCode] = useState([])

  const BACKEND_ENDPOINT = process.env.REACT_APP_BACKEND_ENDPOINT

  const DEFAULT_COUNTRY_CODES_SUGGESTIONS_MAP = {
    Spain: 'ES',
    Portugal: 'PT',
    // Gibraltar: 'GI',
    // Andorra: 'AD'
  }
  const countriesToSuggestAddresses = useMemo(() => {
    const selectedCountry = values?.address?.country?.code
    return selectedCountry
      ? [selectedCountry]
      : Object.values(DEFAULT_COUNTRY_CODES_SUGGESTIONS_MAP)
  }, [values?.address?.country?.code])

  const getProvincesByCountryCode = async countryCode => {
    try {
      const response = await fetch(provincesFile)
      const arrayBuffer = await response.arrayBuffer()
      const data = new Uint8Array(arrayBuffer)
      const workbook = XLSX.read(data, { type: 'array' })

      const sheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[sheetName]

      const jsonData = XLSX.utils.sheet_to_json(worksheet)

      const filteredData = jsonData.filter(
        entry => entry.Country === countryCode,
      )

      const provinces = filteredData.map(entry => entry.Province)
      return provinces
    } catch (error) {
      console.error('Error al cargar el archivo:', error)
    }
  }

  useEffect(() => {
    getProvincesByCountryCode(values.address.country.code)
      .then(provinces => {
        setProvincesByCountryCode(provinces)
      })
      .catch(error => {
        setProvincesByCountryCode([])
      })
  }, [values.address.country])

  const save = () => {
    // NOTE: esta función espera un objeto con las propiedades `address` y
    // `addressSource`. Tenemos que componer esa estructura manualmente para
    // poder obtener la dirección correctamente.
    // TODO: mover la función `createFullAddress` a un archivo fuera de la
    // carpeta `ui/components`.
    const address = createFullAddress({
      ...values,
      addressSource: 'newAddress',
    })

    const payload = {
      address_type_code: 'ORIGIN',
      client_center_code: centerCode,
      client_code: clientCode,
      name: values.address.name ?? '',
      address: address,
      address_2: values.address.city ?? '',
      state_or_province: values.address.province ?? '',
      postal_code:
        values.address.postalCode?.postalCode ??
        values.address.postalCode ??
        '',
      country_code: values.address.country.code ?? '',
      email: values.address?.email ?? '',
      phone: values.address?.phone
        ? values.address?.phonePrefix + values.address?.phone
        : '',
      is_active: true,
      contact_client_code: values?.address?.contactClientCode ?? '',
      comments: values?.comments ?? '',
    }

    if (Object.keys(errors?.address ?? {}).length) return

    const url = `${BACKEND_ENDPOINT}/client-centers/${centerCode}/client-addresses`

    setSavingStatus({
      loading: true,
      error: false,
      success: false,
    })

    authedFetch('POST', url, { data: payload })
      .then(res => {
        setSavingStatus({
          loading: false,
          error: false,
          success: true,
        })
      })
      .catch(error => {
        setSavingStatus({
          loading: false,
          error: true,
          success: false,
        })
      })
  }

  const { data: destinCountries } = useDestinationCountries()

  const countryPhones = countryTelephoneData.allCountries
    .sort((a, b) => {
      if (a.iso2 === 'es') return -1
      if (b.iso2 === 'es') return 1
      if (a.iso2 === 'pt') return -1
      if (b.iso2 === 'pt') return 1
      return a.dialCode - b.dialCode
    })
    .map((country, index, arr) => {
      if (
        index === arr.length - 1 ||
        country.dialCode !== arr[index + 1].dialCode
      ) {
        let countryName = destinCountries?.find(
          ({ code }) => code === country.iso2.toLocaleUpperCase(),
        )
        if (countryName)
          return {
            dialCode: `+${country.dialCode}`,
            countryCode: country.iso2,
            name: countryName ? countryName?.label : country?.name,
          }
        if (country.dialCode === '1')
          return {
            dialCode: `+${country.dialCode}`,
            countryCode: 'us',
            name: countryName
              ? countryName?.label
              : 'Estados Unidos de América',
          }
      }
      return null
    })
    .filter(country => country !== null)

  useEffect(() => {
    const countryPhone = countryPhones.find(
      countryPhone =>
        countryPhone.countryCode ===
        `${
          values.address.country.code.toLowerCase() === 'it'
            ? 'va'
            : values.address.country.code.toLowerCase()
        }`,
    )

    onChange({
      target: {
        name: 'address.phonePrefix',
        value: countryPhone?.dialCode,
      },
    })
  }, [values.address.country.code])

  useEffect(() => {
    onBlur({
      target: { name: 'address.postalCode', value: values.address.postalCode },
    })
  }, [values.address.postalCode])

  return (
    <Box component="section">
      <Box className={classes.fields}>
        <Box className={classes.fieldsRow}>
          <Box className={classes.fieldSM}>
            <InputLabel required>{formatMessage('noun:country')}</InputLabel>
            <Box className={classes.fieldPrefixPhone}>
              <Autocomplete
                options={countries ?? []}
                value={values.address.country}
                isOptionEqualToValue={(option, value) =>
                  option.country_code === value.code ||
                  option.code === value.code
                }
                disableClearable
                name="address.country"
                onChange={(_, country) => {
                  if (country === null) {
                    onChange({
                      target: {
                        name: 'address.country',
                        value: { code: '', label: '' },
                      },
                    })
                    return
                  }
                  const transformedCountry = {
                    code: country.country_code || country.code,
                    label: country.country_name || country.label,
                  }

                  onChange({
                    target: {
                      name: 'address.country',
                      value: transformedCountry,
                    },
                  })
                }}
                onBlur={_event => {
                  onBlur({ target: { name: 'address.country' } })
                }}
                getOptionLabel={option =>
                  option.country_name || option.label || ''
                }
                inputProps={{
                  placeholder: formatMessage('noun:country'),
                  inputProps: { className: 'input-small' },
                  error: errors.address?.country,
                  helperText: errors.address?.country?.code
                    ? formatMessage(errors.address.country.code.key)
                    : null,
                }}
                className={classes.fieldSM}
              />
            </Box>
          </Box>
          <Box className={classes.fieldMD}>
            <GooglePlacesAutocomplete
              placeholder={formatMessage(
                'shipping-send-form:new-address:placeholder',
              )}
              label={formatMessage(
                'shipping-send-form:origin:new-address:address-finder',
              )}
              changeAddress={autofillAddress}
              countries={countriesToSuggestAddresses}
            />
          </Box>
          <TextField
            name="address.address"
            error={errors?.address?.address}
            helperText={
              errors.address?.address
                ? formatMessage(errors.address.address.key)
                : null
            }
            label={formatMessage(
              'shipping-send-form:origin:new-address:address',
            )}
            value={values.address.address}
            onChange={onChange}
            onBlur={onBlur}
            className={classes.fieldMD}
            InputLabelProps={{ required: true }}
            inputProps={{ readOnly: false, maxLength: 200 }}
          />
          <TextField
            name="address.stair"
            label={formatMessage('noun:stair')}
            value={values.address.stair}
            onChange={onChange}
            onBlur={onBlur}
            className={classes.fieldXS}
            inputProps={{ readOnly: false }}
          />
          <TextField
            name="address.floor"
            label={formatMessage('noun:floor')}
            value={values.address.floor}
            onChange={onChange}
            onBlur={onBlur}
            className={classes.fieldXS}
            inputProps={{ readOnly: false }}
          />
          <TextField
            name="address.letter"
            label={formatMessage('noun:letter')}
            value={values.address.letter}
            onChange={onChange}
            onBlur={onBlur}
            className={classes.fieldXS}
            inputProps={{ readOnly: false }}
          />
        </Box>
        <Box className={classes.fieldsRowGrid}>
          <TextField
            name="address.postalCode"
            error={errors?.address?.postalCode}
            helperText={
              errors.address?.postalCode
                ? formatMessage(errors.address.postalCode.key)
                : null
            }
            label={formatMessage(
              'shipping-send-form:origin:new-address:postal-code',
            )}
            value={values.address.postalCode}
            onChange={onChange}
            onBlur={onBlur}
            className={classes.fieldMD}
            InputLabelProps={{ required: true }}
            inputProps={{ readOnly: false, maxLength: 50 }}
            placeholder={
              values.address.country.code === 'ES'
                ? formatMessage(
                    'shipping-send-form:origin:new-address:postal-code-es-placeholder',
                  )
                : values.address.country.code === 'PT'
                ? formatMessage(
                    'shipping-send-form:origin:new-address:postal-code-pt-placeholder',
                  )
                : formatMessage(
                    'shipping-send-form:origin:new-address:postal-code-placeholder',
                  )
            }
          />
          <Box className={classes.fieldMD}>
            {values.address.hasSeveralLocalities ? (
              <>
                {/* NOTE: select con prefijos de localidades de ejemplo */}
                <Select
                  // NOTE: solución temporal para mostrar placeholder en select (https://github.com/mui/material-ui/issues/11069#issuecomment-1238685560)
                  sx={{
                    '& .MuiSelect-select .notranslate::after': formatMessage(
                      'action:select-locality',
                    )
                      ? {
                          content: `"${formatMessage(
                            'action:select-locality',
                          )}"`,
                          opacity: 0.6,
                        }
                      : {},
                  }}
                  error={errors?.address?.city}
                  helperText={
                    errors.address?.city
                      ? formatMessage(errors.address.city.key)
                      : null
                  }
                  value={values.address.city}
                  isRequired
                  className="input-full"
                  labelId={'shipping-send-form:origin:new-address:locality'}
                  label={
                    <>
                      {formatMessage(
                        'shipping-send-form:origin:new-address:locality',
                      )}
                    </>
                  }
                  onChange={event => {
                    const locality = event.target.value
                    onChange({
                      target: { name: 'address.city', value: locality },
                    })
                  }}
                  onBlur={event => {
                    const locality = event.target.value
                    onBlur({
                      target: { name: 'address.city', value: locality },
                    })
                  }}
                >
                  {values.address.localitiesOptions.map(locality => (
                    <MenuItem key={locality} value={locality}>
                      {locality}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText className={classes.helperText}>
                  <WarningCircleFillIcon />
                  <Typography variant="label">
                    {formatMessage(
                      'shipping-send-form:helper-text:several-localities-associate-with-postal-code',
                    )}
                  </Typography>
                </FormHelperText>
              </>
            ) : (
              <Box>
                <TextField
                  name="address.city"
                  error={errors?.address?.city}
                  helperText={
                    errors.address?.city
                      ? formatMessage(errors.address.city.key)
                      : null
                  }
                  label={
                    <>
                      {formatMessage(
                        'shipping-send-form:origin:new-address:locality',
                      )}
                    </>
                  }
                  // NOTE: por temas de prisas, de momento se van a deshabilitar
                  // los campos de localidad y provincia.
                  // TODO: cuando se quieran deshabilitar, utilizar el siguiente
                  // label.
                  // label={
                  //   <Box className={classes.labelTooltip}>
                  //     <span>
                  //       {formatMessage(
                  //         'shipping-send-form:origin:new-address:locality',
                  //       )}
                  //     </span>
                  //     <TooltipButton
                  //       type="primary"
                  //       title={formatMessage(
                  //         'shipping-send-form:label:tooltip-autofill-postal-code',
                  //       )}
                  //       placement="right"
                  //     />
                  //   </Box>
                  // }
                  value={values.address.city}
                  onChange={e => onChange(e)}
                  onBlur={onBlur}
                  className="input-full"
                  InputLabelProps={{ required: true }}
                  inputProps={{
                    maxLength: 50,
                  }}
                  placeholder={formatMessage(
                    'shipping-send-form:origin:new-address:locality',
                  )}
                />
                {zeroResultsGeocode ? (
                  <FormHelperText className={classes.helperText}>
                    <WarningCircleFillIcon />
                    <Typography variant="label">
                      {formatMessage(
                        'shipping-send-form:helper-text:not-found-localities-input-manually',
                      )}
                    </Typography>
                  </FormHelperText>
                ) : null}
              </Box>
            )}
          </Box>
          {provicesByCountryCode?.length ? (
            <Autocomplete
              options={provicesByCountryCode ?? []}
              value={values.address.province}
              isOptionEqualToValue={(option, value) => option === value}
              disableClearable
              name="address.province"
              onChange={(_, province) => {
                onChange({
                  target: {
                    name: 'address.province',
                    value: province,
                  },
                })
              }}
              onBlur={_event => {
                onBlur({ target: { name: 'address.province' } })
              }}
              getOptionLabel={option => option || ''}
              inputProps={{
                required: true,
                placeholder: formatMessage(
                  'shipping-send-form:origin:new-address:province',
                ),
                label: formatMessage(
                  'shipping-send-form:origin:new-address:province',
                ),
                error: errors.address?.province,
                helperText: errors.address?.province
                  ? formatMessage(errors.address.country.code.key)
                  : null,
              }}
              className={classes.fieldMD}
            />
          ) : (
            <Box>
              <TextField
                name="address.province"
                error={errors?.address?.province}
                helperText={
                  errors.address?.province
                    ? formatMessage(errors.address.province.key)
                    : null
                }
                label={formatMessage(
                  'shipping-send-form:origin:new-address:province',
                )}
                // NOTE: por temas de prisas, de momento se van a deshabilitar
                // los campos de localidad y provincia.
                // TODO: cuando se quieran deshabilitar, utilizar el siguiente
                // label.
                // label={
                //   <Box className={classes.labelTooltip}>
                //     <span>
                //       {formatMessage(
                //         'shipping-send-form:origin:new-address:province',
                //       )}
                //     </span>
                //     <TooltipButton
                //       type="primary"
                //       title={formatMessage(
                //         'shipping-send-form:label:tooltip-autofill-postal-code',
                //       )}
                //       placement="right"
                //     />
                //   </Box>
                // }
                value={values.address.province}
                onChange={onChange}
                onBlur={onBlur}
                className="input-full"
                InputLabelProps={{ required: true }}
                inputProps={{
                  maxLength: 50,
                }}
                placeholder={formatMessage(
                  'shipping-send-form:origin:new-address:province',
                )}
              />
              {zeroResultsGeocode ? (
                <FormHelperText className={classes.helperText}>
                  <WarningCircleFillIcon />
                  <Typography variant="label">
                    {formatMessage(
                      'shipping-send-form:helper-text:not-found-localities-input-manually',
                    )}
                  </Typography>
                </FormHelperText>
              ) : null}
            </Box>
          )}
        </Box>

        <Box className={classes.fieldsRow}>
          <TextField
            name="comments"
            label={formatMessage(
              'shipping-send-form:label:additional-comments',
            )}
            multiline
            maxRows={3}
            // {...getFieldProps('comments', values, fieldErrors, touched, t)}
            onChange={onChange}
            onBlur={onBlur}
            className={classes.fieldComments}
            value={values.comments}
            placeholder={formatMessage(
              'shipping-send-form:placeholder:additional-comments',
            )}
          />
        </Box>

        <Typography
          variant="h3"
          className={clsx('secondaryTitle', classes.title)}
        >
          {formatMessage('noun:contact-data')}
        </Typography>
        <Box className={classes.fieldsRow}>
          <TextField
            name="address.name"
            error={errors?.address?.name}
            helperText={
              errors.address?.name
                ? formatMessage(errors.address.name.key)
                : null
            }
            label={formatMessage('noun:name-company')}
            value={values.address.name}
            onChange={onChange}
            onBlur={onBlur}
            className={classes.fieldMD}
            InputLabelProps={{ required: true }}
            inputProps={{ readOnly: false, maxLength: 50 }}
          />

          <Box className={classes.fieldMD}>
            <InputLabel required={isPhoneRequired}>
              {formatMessage('noun:mobile-phone')}
            </InputLabel>
            <Box className={classes.fieldPrefixPhone}>
              {/* NOTE: select con prefijos de teléfono de ejemplo */}
              {/* TODO: cuando se aborde la tarea del prefijo, descomentar el Select. */}
              <Autocomplete
                noSearchIcon
                options={countryPhones ?? []}
                value={values?.address.phonePrefix ?? '+34'}
                isOptionEqualToValue={(option, value) =>
                  option?.dialCode === value
                }
                disableClearable
                name="address.phonePrefix"
                onChange={(_, phonePrefix) => {
                  if (phonePrefix === null) {
                    onChange({
                      target: {
                        name: 'address.phonePrefix',
                        value: '+34',
                      },
                    })
                    return
                  }

                  onChange({
                    target: {
                      name: 'address.phonePrefix',
                      value: phonePrefix,
                    },
                  })
                }}
                getOptionLabel={option => {
                  let findCountry = countryPhones.find(
                    ({ dialCode }) => dialCode === option,
                  )
                  if (option?.dialCode) return option?.dialCode
                  return `${findCountry?.dialCode}`
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    style={{ height: '100%', width: '100%', padding: 15 }}
                    key={option?.dialCode}
                  >
                    {`${option?.dialCode} | ${option?.name}`}
                  </Box>
                )}
                sx={classes.fieldPrefixPhoneSelect}
                className={classes.fieldPrefixPhoneSelect}
                inputProps={{
                  placeholder: formatMessage('noun:phonePrefix'),
                }}
              />
              <TextField
                name="address.phone"
                label={null}
                type="tel"
                value={values.address.phone}
                onChange={onChange}
                onBlur={onBlur}
                error={errors?.address?.phone}
                helperText={
                  errors.address?.phone
                    ? formatMessage(errors.address.phone.key)
                    : null
                }
                InputLabelProps={{ required: isPhoneRequired }}
                // TODO: cuando se aborde la tarea del prefijo, utilizar la siguiente prop (className).
                // className={(classes.fieldMD, classes.fieldPrefixPhoneInput)}
                className={classes.fieldMD}
                inputProps={{ readOnly: false, maxLength: 15 }}
              />
            </Box>
          </Box>
        </Box>
        <Box className={classes.fieldsRow}>
          <TextField
            name="address.email"
            label={formatMessage('noun:email')}
            type="email"
            value={values.address.email}
            onChange={onChange}
            onBlur={onBlur}
            className={classes.fieldMD}
            inputProps={{ readOnly: false }}
          />
          <TextField
            name="address.contactClientCode"
            label={formatMessage('noun:contact-client-code')}
            value={values.address?.contactClientCode}
            onChange={onChange}
            onBlur={onBlur}
            className={classes.fieldMD}
            inputProps={{ readOnly: false, maxLength: 50 }}
          />
        </Box>
      </Box>
      {savingStatus.success ? (
        <Alert severity="success" className={classes.alert}>
          {formatMessage('shipping-send-form:alert-address-saved')}
        </Alert>
      ) : null}

      {savingStatus.error ? (
        <Alert severity="error" className={classes.alert}>
          {formatMessage('shipping-send-form:alert-address-saved-error')}
        </Alert>
      ) : null}

      {savingStatus.success === false ? (
        <Button
          className={classes.loadingButton}
          disabled={
            !isValidPhoneNumber(
              values?.address?.phonePrefix + values?.address?.phone,
            )
          }
          onClick={() => {
            save()
          }}
          size="smaller"
          variant="outlined"
        >
          {savingStatus.error === false
            ? savingStatus.loading
              ? formatMessage('message:saving')
              : formatMessage('action:save-address-my-agenda')
            : formatMessage('shipping-send-form:try-again')}
        </Button>
      ) : null}
    </Box>
  )
}

Address.propTypes = propTypes

export default Address
